import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { MdModeEdit, MdOutlineLocalPrintshop } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { ArrowUpDown, MoveDown, MoveUp } from "lucide-react";
import { FiSearch } from "react-icons/fi";
import useDebounce, {
  formatDate,
  formatDateTime,
  IsServiceTypeVendor,
  truncateString,
  useDecodedTokenResult,
} from "../../lib/utils";
import {
  useGetAllVendorNameOrIdQuery,
  useGetOrdersMutation,
} from "../../api/api";
import { useDispatch } from "react-redux";
import { setAppState } from "../../redux/slices/appState";
import { Input } from "../../@/components/ui/input";
import {
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
} from "../../@/components/ui/table";
import SkeletonTableLoading from "../../skeletonLoading/SkeletonTableLoading";
import Pagination from "../../components/Pagination";
import GenericDropdown from "../../components/GenericDropDown";
import ExportOrders from "../../components/ExportOrders";
import { useToast } from "../../@/components/ui/use-toast";
import PrintPage from "../../components/PrintPage";
import SingleDatePicker from "../../components/SingleDatePicker";
import { STATUS, USER_ROLE } from "../../lib/constants";
import { isMobileScreen } from "../../lib/utils";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface Data {
  id: number;
  vendorId: number;
  vendorName: string;
  customerId: number;
  statusId: number;
  createdAt: Date;
  totalAmount: number;
  customerName: string;
  statusName: string;
}

const OrdersListView: React.FC = () => {
  const [selected, setSelected] = useState<any>({
    name: "All Orders",
    id: undefined,
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const { vendorId, role, serviceTypeId } = useDecodedTokenResult();
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const isVendorService = IsServiceTypeVendor();

  const options = useMemo(() => {
    const allOptions = [
      { name: "All Orders", id: undefined },
      { name: "Activated", id: STATUS.OrderActivated },
      { name: "Confirmed", id: STATUS.OrderConfirmed },
      { name: "Dispatched", id: STATUS.OrderDispatched },
      { name: "Delivered", id: STATUS.OrderDelivered },
      { name: "Returned", id: STATUS.OrderReturned },
      { name: "Cancelled", id: STATUS.OrderCancelled },
    ];

    if (role === USER_ROLE.ADMIN) {
      allOptions.push({
        name: "Completed",
        id: STATUS.OrderCompleted,
      });
    }

    const modifiedOptions = allOptions.map((option) => {
      if (role === "VENDOR" && isVendorService && option.name === "Delivered") {
        return { name: "Completed", id: STATUS.OrderCompleted };
      }
      return option;
    });

    if (role === "VENDOR" && isVendorService) {
      return modifiedOptions.filter((option) =>
        [
          "All Orders",
          "Activated",
          "Confirmed",
          "Completed",
          "Cancelled",
        ].includes(option.name)
      );
    }

    return modifiedOptions;
  }, [role, serviceTypeId]);

  const [data, setData] = useState<Data[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, _] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const navigate = useNavigate();
  const toast = useToast();

  const [getOrders, { error, isLoading: ordersLoading }] =
    useGetOrdersMutation();
  const { ...getAllVendor } = useGetAllVendorNameOrIdQuery(undefined, {
    skip: role === "ADMIN" ? false : true,
  });
  const [selectedVendor, setSelectedVendor] = useState("");
  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "desc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "createdAt"
  );

  const dispatch = useDispatch();

  const handleSorting = (column: string) => {
    if (sortingColumn === column) {
      setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
    } else {
      setSortingColumn(column);
      setSortingOrder("asc");
    }
    setPageNumber(1);
  };

  const handleVendorChange = (value: string) => {
    const vendorName =
      vendorList.find((vendor) => vendor.id === parseInt(value))?.company || "";

    if (value === "-1") {
      setSelectedVendorName("");
      setSelectedVendor("");
    } else {
      setSelectedVendorName(truncateString(vendorName, 15));
      setSelectedVendor(value);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const response: any = await getOrders({
        getOrderRequestDto: {
          vendorId:
            role === "ADMIN"
              ? selectedVendor === ""
                ? undefined
                : parseInt(selectedVendor)
              : vendorId,
          searchQuery:
            debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
          pageNumber,
          pageSize,
          getTotalCount: true,
          statusId: selected.id,
          sortingOrder,
          sortingColumn,
          startDate: startDate
            ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          endDate: endDate
            ? formatDateTime(
                endDate.setHours(23, 59, 59, 999),
                "YYYY-MM-DDTHH:mm:ssZ"
              )
            : undefined,
        },
      }).unwrap();

      setData(response?.data?.orders || []);
      setTotalCount(response?.data?.totalCount || 0);
    } catch (err) {
      console.error("Failed to get products: ", err);
    }
  }, [
    getOrders,
    debouncedSearchQuery,
    selected,
    pageNumber,
    vendorId,
    sortingColumn,
    sortingOrder,
    startDate,
    endDate,
    selectedVendor,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData, pageNumber, startDate, endDate, selected]); // Include startDate and endDate in the dependencies

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
    setSearchTerm(event.target.value);
  };

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const handleEditOrder = (orderId: number) => {
    navigate(`/edit-order/${orderId}`);
  };

  const handlePrintOrder = (OrderId: number) => {
    const printWindow = window.open(
      `/printPdf?orderId=${OrderId}`,
      "PrintWindow",
      "height=600,width=800,top=100,left=100,resizable=no,scrollbars=no,toolbar=no,menubar=no,location=no,status=no"
    );

    if (printWindow) {
      printWindow.focus();
    } else {
      alert("Popup blocked! Please allow popups for this website.");
    }
  };

  const columns = useMemo<ColumnDef<Data>[]>(() => {
    const baseColumns: ColumnDef<Data>[] = [
      {
        accessorKey: "id",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("id")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Order Number{" "}
            {sortingColumn === "id" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      ...(role === "ADMIN"
        ? [
            {
              accessorKey: "vendorName",
              header: ({ column }: { column: any }) => (
                <div
                  onClick={() => handleSorting("vendorName")}
                  className="cursor-pointer flex gap-1 items-center"
                >
                  Vendor{" "}
                  {sortingColumn === "vendorName" ? (
                    sortingOrder === "asc" ? (
                      <MoveUp size={14} />
                    ) : (
                      <MoveDown size={14} />
                    )
                  ) : (
                    <ArrowUpDown size={14} />
                  )}
                </div>
              ),
              size: 130,
            },
          ]
        : []),
      {
        accessorKey: "customerName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("customerName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Customer{" "}
            {sortingColumn === "customerName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
      },
      {
        accessorKey: "totalAmount",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("totalAmount")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Total{" "}
            {sortingColumn === "totalAmount" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 130,
        cell: ({ cell }) => {
          const value = parseFloat(cell.getValue() as string);
          // const totalPrice = Math.floor(value * 100) / 100;
          return <span>${value.toFixed(2)}</span>;
        },
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("createdAt")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Added On{" "}
            {sortingColumn === "createdAt" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        cell: ({ cell }) => (
          <span>{formatDate(cell.getValue() as string, "dd MMM yyyy")}</span>
        ),

        size: 130,
      },
      {
        accessorKey: "statusName",
        header: ({ column }) => (
          <div
            onClick={() => handleSorting("statusName")}
            className="cursor-pointer flex gap-1 items-center"
          >
            Status{" "}
            {sortingColumn === "statusName" ? (
              sortingOrder === "asc" ? (
                <MoveUp size={14} />
              ) : (
                <MoveDown size={14} />
              )
            ) : (
              <ArrowUpDown size={14} />
            )}
          </div>
        ),
        size: 100,
        cell: ({ cell }) => (
          <span
            className={`px-2 py-1 rounded-full text-xs ${
              cell.getValue() === "Drafted"
                ? "bg-processingStatusBgColor text-processingStatusTextColor"
                : cell.getValue() === "Activated"
                ? "bg-green-200 text-green-700"
                : cell.getValue() === "Delivered"
                ? "bg-deliveredStatusBgColor text-deliveredStatusTextColor"
                : cell.getValue() === "Completed"
                ? "bg-deliveredStatusBgColor text-deliveredStatusTextColor"
                : cell.getValue() === "Cancelled"
                ? "bg-cancelledStatusBgColor text-cancelledStatusTextColor"
                : cell.getValue() === "Confirmed"
                ? "bg-yellow-200 text-yellow-700"
                : "bg-gray-200 text-gray-700"
            }`}
          >
            {cell.getValue() as string}
          </span>
        ),
        meta: {
          isTooltip: false,
        },
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <div className="flex space-x-2 justify-center">
            <button
              onClick={() => {
                dispatch(setAppState({ routeName: "edit Order" }));
                handleEditOrder(row.original.id);
              }}
            >
              <MdModeEdit className="text-[#72767E] hover:text-black" />
            </button>
            <button
              onClick={() => {
                handlePrintOrder(row.original.id);
              }}
            >
              <MdOutlineLocalPrintshop />
            </button>
          </div>
        ),
        meta: {
          isTooltip: false,
        },
        size: 80,
      },
    ];

    return baseColumns;
  }, [sortingColumn, sortingOrder, role]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    },
  });

  const vendorList = getAllVendor.data?.data || [];

  const filteredData = useMemo(() => {
    if (role === "VENDOR" && serviceTypeId > 2) {
      return data.filter((order: Data) =>
        ["Activated", "Confirmed", "Completed", "Cancelled"].includes(
          order.statusName
        )
      );
    }
    return data;
  }, [data, role, serviceTypeId]);

  return (
    <div className="overflow-hidden h-auto pl-4 pt-2 pr-4 ">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div
          className={`flex justify-between bg-white rounded-lg border p-[1px] border-gray-300 w-full md:w-auto ${
            isMobileScreen ? "overflow-x-auto" : ""
          }`}
        >
          {options.map((option) => (
            <button
              key={option.name}
              onClick={() => {
                setPageNumber(1);
                setSelected(option);
              }}
              className={`flex-1 w-[4.5rem] p-2 text-center text-[10px] ${
                selected.name === option.name
                  ? "bg-customGreenLight text-customGreenColor font-base"
                  : "text-gray-500 hover:bg-gray-100"
              } rounded-lg ${
                option.name === "All Product" ? "rounded-l-lg" : ""
              } ${option.name === "Draft" ? "rounded-r-lg" : ""}`}
            >
              {option.name}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 w-full md:w-auto items-center flex-wrap flex-col sm:flex-row">
          <div className="w-full sm:w-auto">
            <ExportOrders
              key={totalCount}
              selectedStatus={selected}
              datefilter={{
                startDate: startDate,
                endDate: endDate,
              }}
              vendorIdbyProp={parseInt(selectedVendor)}
              totalCount={totalCount}
              sortingOrder={sortingOrder}
              sortingColumn={sortingColumn}
            />
          </div>

          {role === "ADMIN" && (
            <div className="w-full sm:w-auto">
              <GenericDropdown
                labelValue={selectedVendorName}
                onChange={handleVendorChange}
                options={[
                  ...vendorList.map((vendor: any) => ({
                    value: vendor.id.toString(),
                    label: vendor.company,
                  })),
                ]}
                placeholder="Select a vendor"
                error={false}
                className="text-black bg-white h-9 justify-center items-center w-full"
                showAllOption={true}
              />
            </div>
          )}
          <div className="relative flex justify-center text-customGrayText items-center  gap-2 w-full md:w-auto">
            <FiSearch className="absolute left-2 text-lg " />
            <Input
              placeholder="Search order or customer"
              className="text-sm text-left h-9 pl-8"
              onChange={handleSearchChange}
            />
          </div>
          <div className="w-full sm:w-auto">
            <SingleDatePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateSelect={setStartDate}
              onEndDateSelect={setEndDate}
            />
          </div>
        </div>
      </div>
      {ordersLoading ? (
        <div className="overflow-y-auto h-[calc(100vh-12rem)] mt-4">
          <div className="overflow-x-auto bg-white rounded-lg shadow h-full">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-customGreenColor text-white"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        onClick={() => handleSorting(header.id)}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : (
        <div className="overflow-y-auto h-[calc(100vh-12rem)]">
          <div className="overflow-x-auto bg-white rounded-lg shadow h-full">
            <Table>
              <TableHeader className="sticky top-0">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="bg-customGreenColor text-white"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        onClick={() => handleSorting(header.id)}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="text-center">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          style={{
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                          }}
                          isTooltip={
                            cell.column.columnDef.meta?.isTooltip ?? true
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
      {data.length > 0 && (
        <div
          className={`flex justify-between items-center mt-2 w-auto ${
            isMobileScreen ? "flex-col" : ""
          }`}
        >
          <div className="mb-2 text-[#667085]">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(totalCount / pageSize)}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default OrdersListView;
